<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="680px">
    <v-card class="profile-dialog-card entity-form-dialog term-form-dialog">
      <div class="profile-dialog-header entity-form-dialog__header">
        <div class="entity-form-dialog__title">
          <v-avatar size="44" color="green lighten-5"><v-icon color="primary">mdi-calendar-plus</v-icon></v-avatar>
          <div><span>Report cards</span><h2>Create term</h2><p>Choose a department and one of its academic years.</p></div>
        </div>
        <v-btn icon aria-label="Close create term dialog" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <v-divider />
      <v-card-text class="profile-dialog-body entity-form-dialog__body">
        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-school-outline</v-icon></span>
            <div><h3>Term details</h3><p>Select the department first to see only its academic years.</p></div>
          </div>
          <v-row>
            <v-col cols="12"><v-text-field v-model="Term.name" outlined autofocus dense label="Term name *" placeholder="Fall term" :hide-details="nameErrors.length === 0" :error-messages="nameErrors" /></v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete v-model="Term.DepartmentId" :items="departments" item-text="name" item-value="id" outlined dense clearable label="Department *" :loading="optionsLoading" :disabled="isYearLocked" :hide-details="departmentErrors.length === 0" :error-messages="departmentErrors" no-data-text="No departments found" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete v-model="Term.YearId" :items="filteredYears" item-text="name" item-value="id" outlined dense clearable label="Academic year *" :loading="optionsLoading" :disabled="!Term.DepartmentId || isYearLocked" :hide-details="yearErrors.length === 0" :error-messages="yearErrors" no-data-text="No academic years found for this department" />
            </v-col>
          </v-row>
        </section>
        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-calendar-range</v-icon></span>
            <div><h3>Term period</h3><p>Enter the exact start and end dates for this term.</p></div>
          </div>
          <v-row>
            <v-col cols="12" sm="6"><v-text-field v-model="Term.startDate" type="date" outlined dense label="Start date *" :hide-details="startDateErrors.length === 0" :error-messages="startDateErrors" /></v-col>
            <v-col cols="12" sm="6"><v-text-field v-model="Term.endDate" type="date" outlined dense label="End date *" :hide-details="endDateErrors.length === 0" :error-messages="endDateErrors" /></v-col>
          </v-row>
        </section>
      </v-card-text>
      <v-divider />
      <v-card-actions class="profile-dialog-actions entity-form-dialog__actions">
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="saving" @click="submit">Create term</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import termService from "../service.js";
import yearService from "@/modules/ReportCard/Year/service.js";
import classesService from "@/modules/Classes/service.js";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

export default {
  name: "create-term",
  mixins: [validationMixin],
  props: {
    createDialog: { default: false },
    createFromStore: { type: Boolean },
    selectedYear: { type: Object, default: null },
  },
  data() {
    return { dialog: false, saving: false, optionsLoading: false, departments: [], years: [], Term: this.emptyTerm() };
  },
  validations: { Term: { name: { required }, DepartmentId: { required }, YearId: { required }, startDate: { required }, endDate: { required } } },
  computed: {
    isYearLocked() { return !!(this.selectedYear && this.selectedYear.id); },
    filteredYears() {
      if (!this.Term.DepartmentId) return [];
      return this.years.filter((year) => String(this.yearDepartmentId(year)) === String(this.Term.DepartmentId));
    },
    nameErrors() { return this.requiredErrors("name"); },
    departmentErrors() { return this.requiredErrors("DepartmentId"); },
    yearErrors() { return this.requiredErrors("YearId"); },
    startDateErrors() {
      const errors = this.requiredErrors("startDate", "Start date is required");
      if (!errors.length && this.hasInvalidDateRange) errors.push("Start date cannot be after end date");
      return errors;
    },
    endDateErrors() {
      const errors = this.requiredErrors("endDate", "End date is required");
      if (!errors.length && this.hasInvalidDateRange) errors.push("End date cannot be before start date");
      return errors;
    },
    hasInvalidDateRange() { return !!(this.Term.startDate && this.Term.endDate && this.Term.startDate > this.Term.endDate); },
  },
  watch: {
    createDialog(val) { this.dialog = val; if (val) this.loadOptions(); },
    selectedYear: { deep: true, handler() { if (this.dialog) this.applySelectedYear(); } },
    "Term.DepartmentId"(departmentId, previousDepartmentId) {
      if (!previousDepartmentId || String(departmentId) === String(previousDepartmentId)) return;
      if (!this.filteredYears.some((year) => String(year.id) === String(this.Term.YearId))) this.Term.YearId = null;
    },
  },
  methods: {
    emptyTerm() { return { name: null, DepartmentId: null, YearId: null, startDate: null, endDate: null }; },
    yearDepartmentId(year) { return year && (year.ClassId || (year.Class && year.Class.id)); },
    requiredErrors(field, message = null) {
      const validation = this.$v.Term[field];
      if (!validation.$dirty || validation.required) return [];
      return [message || this.$t("validations.fieldIsRequired")];
    },
    async loadOptions() {
      this.optionsLoading = true;
      try {
        const [departmentsResponse, yearsResponse] = await Promise.all([classesService.getAll({ limit: 10000 }), yearService.getAll({ limit: 10000 })]);
        this.departments = departmentsResponse.data || [];
        this.years = yearsResponse.data || [];
        this.applySelectedYear();
      } catch (error) {
        this.toast("error", this.errorMessage(error, "Unable to load departments and academic years"));
      } finally { this.optionsLoading = false; }
    },
    applySelectedYear() {
      if (!this.selectedYear || !this.selectedYear.id) return;
      this.Term.DepartmentId = this.yearDepartmentId(this.selectedYear);
      this.Term.YearId = this.selectedYear.id;
    },
    close() { this.Term = this.emptyTerm(); this.$v.$reset(); this.$emit("close"); },
    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid || this.hasInvalidDateRange) { this.toast("error", this.$t("toastMessage.text.invalidFields")); return; }
      this.saving = true;
      try {
        const payload = { ...this.Term };
        delete payload.DepartmentId;
        await termService.create(payload);
        this.toast("success", "Term is created");
        this.$emit("submit");
        this.close();
      } catch (error) { this.toast("error", this.errorMessage(error, "Unable to create term")); }
      finally { this.saving = false; }
    },
    errorMessage(error, fallback) { return error?.response?.data?.message || error?.data?.message || error?.message || fallback; },
    toast(icon, title) { this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 }).fire({ icon, title }); },
  },
};
</script>
